<template>
  <ClientOnly>
    <div
      id="up-navigation-navbar-container"
      class="fixed bg-white top-0 w-[calc(100vw-theme(spacing.18))] left-18 z-20 tracking-normal"
    >
      <!------------------ Topbar ------------------>
      <UpNavTopBar>
        <!------------------ Avatar Indicator ------------------>
        <template #avatarIndicator>
          <OnClickOutside
            :options="{ ignore: ['#up-nav-avatar-menu'] }"
            @trigger="setNavAvatarMenuVisibility(false)"
          >
            <AvatarIndicator
              :user-initials="userInitials"
              :is-user-impersonated="Boolean(user?.is_impersonated)"
              @click="toggleNavAvatarMenuVisibility"
            />
          </OnClickOutside>
        </template>
        <!------------------ Avatar Menu ------------------>
        <template #avatarMenu>
          <UpNavAvatarMenu
            v-if="isNavAvatarMenuVisible"
            :user-name="user?.name ?? ''"
            :user-role="user?.role_name ?? ''"
            :time-zone="t('common.timezoneLabel', { zone: avatarMenuTimeZone })"
          >
            <template #avatarMenuIndicator>
              <AvatarIndicator
                size="2.5rem"
                :user-initials="userInitials"
                :is-user-impersonated="Boolean(user?.is_impersonated)"
              />
            </template>
            <!------------------ Avatar Menu Options ------------------>
            <template #avatarMenuOptions>
              <UpNavAvatarMenuOptions
                v-for="option in avatarMenuOptions"
                :key="option?.id"
                :is-active="isAvatarMenuOptionActive(option)"
                :option-name="t(option?.translationKey ?? '')"
                :data-intercom-target="option.dataTarget"
                @option-click="handleAvatarMenuOptionClick(option)"
              />
            </template>
          </UpNavAvatarMenu>
        </template>
      </UpNavTopBar>
      <!------------------ Navbar ------------------>
      <UpNavBar v-if="navBarOptions">
        <!------------------ Navbar Options ------------------>
        <template #navbarOptions>
          <OnClickOutside
            :options="{
              ignore: ['#up-nav-bar-dropdown'],
            }"
            class="flex gap-7"
            @trigger="setNavDropdownVisibility(false)"
          >
            <UpNavBarOptions
              v-for="option in navBarOptions"
              :id="option?.id"
              :key="option?.id"
              :path="option?.path"
              :is-active="option?.id === activeNavBarOption?.navbarOptionId"
              :display-text="t(option.translationKey ?? '')"
              :has-dropdown-options="hasDropdownOptions(option?.id ?? 0)"
              :data-intercom-target="option.dataTarget"
              @click="handleNavBarOptionClick(option)"
            />
          </OnClickOutside>
        </template>
        <!------------------ Navbar Dropdown ------------------>
        <template #navbarDropdown>
          <UpNavBarDropdown v-if="isNavDropdownVisible">
            <!------------------ Navbar Dropdown Options ------------------>
            <template #dropdownOptions>
              <UpNavBarDropdownOptions
                v-for="option in navBarDropdownOptions"
                :key="option?.id"
                :is-active="
                  option?.id === activeNavBarDropdownOption?.id ||
                  option?.id === activeNavBarOption?.navbarDropdownOptionId
                "
                :display-text="t(option.translationKey ?? '')"
                :data-intercom-target="option.dataTarget"
                @click="handleNavBarDropdownOptionClick(option)"
              >
                <template v-if="option.tags?.length" #tags>
                  <UpNavDropdownOptionTags
                    v-for="tag in option.tags"
                    :key="tag.translationKey"
                    :display-text="t(tag.translationKey)"
                  />
                </template>
              </UpNavBarDropdownOptions>
            </template>
            <template #supportCard>
              <UpNavSupportCard
                :heading-text="t('navbar.support.heading')"
                :body-text="t('navbar.support.body')"
                :contact-button-text="t('navbar.support.contactButtonText')"
                @contact-button-click="toggleIntercomHelp"
              />
            </template>
          </UpNavBarDropdown>
        </template>
        <!------------------ Navbar Breadcrumbs ------------------>
        <template #breadCrumbs>
          <UpNavBarBreadCrumbs
            v-if="activeNavBarDropdownOption"
            :display-text="t(activeNavBarDropdownOption?.translationKey ?? '')"
          />
        </template>
      </UpNavBar>
    </div>
    <div
      id="up-navigation-navbar-container__buffer"
      :class="
        navBarOptions ? (activeNavBarDropdownOption ? 'h-41' : 'h-31') : 'h-18'
      "
    />
  </ClientOnly>
</template>
<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { OnClickOutside } from "@vueuse/components";
import { useUserStore } from "~/store/user.ts";
import { useUpNavigationStore } from "~/store/upNavigationStore.ts";
import UpNavBar from "~/components/header/UpNavigationNavbar/UpNavBar.vue";
import UpNavTopBar from "~/components/header/UpNavigationNavbar/UpNavTopBar.vue";
import UpNavAvatarMenu from "~/components/header/UpNavigationNavbar/UpNavAvatarMenu.vue";
import UpNavBarOptions from "~/components/header/UpNavigationNavbar/UpNavBarOptions.vue";
import UpNavSupportCard from "~/components/header/UpNavigationNavbar/UpNavSupportCard.vue";
import UpNavBarDropdown from "~/components/header/UpNavigationNavbar/UpNavBarDropdown.vue";
import AvatarIndicator from "~/components/header/UpNavigationNavbar/UpNavAvatarIndicator.vue";
import UpNavBarBreadCrumbs from "~/components/header/UpNavigationNavbar/UpNavBreadCrumbs.vue";
import UpNavAvatarMenuOptions from "~/components/header/UpNavigationNavbar/UpNavAvatarMenuOptions.vue";
import UpNavBarDropdownOptions from "~/components/header/UpNavigationNavbar/UpNavBarDropdownOptions.vue";
import UpNavDropdownOptionTags from "~/components/header/UpNavigationNavbar/UpNavDropdownOptionTags.vue";

const { t } = useI18n();
const userStore = useUserStore();
const { userInitials, user } = storeToRefs(userStore);
const upNavigationStore = useUpNavigationStore();
const {
  setNavDropdownVisibility,
  setNavAvatarMenuVisibility,
  toggleNavAvatarMenuVisibility,
  handleAvatarMenuOptionClick,
  handleNavBarOptionClick,
  handleNavBarDropdownOptionClick,
  hasDropdownOptions,
} = upNavigationStore;

const {
  avatarMenuOptions,
  navBarOptions,
  isNavDropdownVisible,
  isNavAvatarMenuVisible,
  activeNavBarOption,
  navBarDropdownOptions,
  activeNavBarDropdownOption,
  avatarMenuTimeZone,
  isAvatarMenuOptionActive,
} = storeToRefs(upNavigationStore);

const { toggleIntercomHelp } = useIntercomHelp();
</script>
